import { useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'

interface ItemAplicacao {
  pnvLin: number; itsCod: number | null; descricao: string
  quantidade: number; valorTotal: number
  aplCodAtual: string; aplDesAtual: string
  tipSerAtual: number | null; tipSerDesAtual: string
  temRateio: boolean
}

interface NotaAplicacao {
  empcod: number; serie: string; numero: number; cliCod: string; cliNom: string
  modelo: string; chave: string | null
  dataEmissao: string | null; dataRecebimento: string | null
  itens: ItemAplicacao[]
}

interface NotaListaItem {
  empcod: number; serie: string; numero: number; cliCod: string; cliNom: string
  modelo: string; dataEmissao: string | null; dataRecebimento: string | null; valorTotal: number
}

interface LinhaRateio {
  parcela: number; data: string | null
  centroCustoCod: string; centroCustoDes: string
  aplCod: string; aplDes: string
  percentual: number; valor: number
  novaAplCod: string; novaAplDes: string; novoPerc: string
}

function parsePerc(v: string) {
  const n = Number(String(v).replace(/\./g, '').replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}

function fmtMoeda(v: number) {
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtDataBr(v: string | null) {
  if (!v) return '  /  /  '
  return v.slice(0, 10).split('-').reverse().join('/')
}

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

function renderAplicacao(item: any) {
  return (
    <>
      <div className="nat-item-linha1"><span className="nat-item-cod">{item.apl_cod}</span></div>
      <div className="nat-item-dsc">{item.apl_des}</div>
    </>
  )
}
function descAplicacao(item: any) {
  return `${item.apl_cod} - ${item.apl_des}`
}
function renderServico(item: any) {
  return (
    <>
      <div className="nat-item-linha1"><span className="nat-item-cod">{item.sercod}</span></div>
      <div className="nat-item-dsc">{item.serdes}</div>
    </>
  )
}
function descServico(item: any) {
  return `${item.sercod} - ${item.serdes}`
}

export default function AlterarAplicacaoNotaFiscal() {
  const { empresaCod } = useEmpresa()
  const [chave, setChave] = useState('')
  const [serie, setSerie] = useState('')
  const [numero, setNumero] = useState('')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')

  const [buscando, setBuscando] = useState(false)
  const [erro, setErro] = useState('')
  const [lista, setLista] = useState<NotaListaItem[] | null>(null)
  const [nota, setNota] = useState<NotaAplicacao | null>(null)

  const [novaAplGeralCod, setNovaAplGeralCod] = useState('')
  const [novaAplGeralDes, setNovaAplGeralDes] = useState('')
  const [overrideApl, setOverrideApl] = useState<Record<number, { cod: string; des: string }>>({})
  const [overrideServ, setOverrideServ] = useState<Record<number, { cod: string; des: string }>>({})

  const [salvando, setSalvando] = useState(false)
  const [sucesso, setSucesso] = useState('')
  const [avisos, setAvisos] = useState<string[]>([])

  // Janela "Alterar rateio" (WACEAPL5): aplicação e/ou percentual de cada linha do rateio do item
  const [rateioItem, setRateioItem] = useState<ItemAplicacao | null>(null)
  const [rateioLinhas, setRateioLinhas] = useState<LinhaRateio[]>([])
  const [rateioErro, setRateioErro] = useState('')
  const [rateioSalvando, setRateioSalvando] = useState(false)

  async function abrirRateio(it: ItemAplicacao) {
    if (!nota) return
    setRateioErro(''); setRateioLinhas([]); setRateioItem(it)
    try {
      const r = await api.get('/erp/alterar-aplicacao-nota/rateio', {
        params: { empcod: nota.empcod, serie: nota.serie, numero: nota.numero, cliCod: nota.cliCod, pnvLin: it.pnvLin },
      })
      setRateioLinhas((r.data as any[]).map(l => ({
        ...l, novaAplCod: '', novaAplDes: '',
        novoPerc: Number(l.percentual).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 }),
      })))
    } catch (e: any) {
      setRateioErro(e?.response?.data?.error || 'Erro ao buscar o rateio.')
    }
  }

  const rateioTotal = rateioLinhas.reduce((s, l) => s + l.valor, 0)
  const rateioSomaPerc = rateioLinhas.reduce((s, l) => s + parsePerc(l.novoPerc), 0)
  const rateioPercOk = Math.abs(rateioSomaPerc - 100) < 0.001
  // Sem mudança de percentual os valores gravados ficam como estão (nem sempre são exatamente total × %)
  const rateioMudouPerc = rateioLinhas.some(l => Math.abs(parsePerc(l.novoPerc) - l.percentual) > 0.00001)
  function valorLinhaRateio(idx: number) {
    if (!rateioMudouPerc) return rateioLinhas[idx].valor
    if (idx === rateioLinhas.length - 1) {
      const antes = rateioLinhas.slice(0, -1).reduce((s, l) => s + Math.round(rateioTotal * parsePerc(l.novoPerc)) / 100, 0)
      return Math.round((rateioTotal - antes) * 100) / 100
    }
    return Math.round(rateioTotal * parsePerc(rateioLinhas[idx].novoPerc)) / 100
  }

  async function salvarRateio() {
    if (!nota || !rateioItem) return
    if (!rateioPercOk) { setRateioErro(`A soma dos percentuais deve ser 100% (está ${rateioSomaPerc.toFixed(2).replace('.', ',')}%).`); return }
    setRateioErro(''); setRateioSalvando(true)
    try {
      await api.post('/erp/alterar-aplicacao-nota/rateio', {
        empcod: nota.empcod, serie: nota.serie, numero: nota.numero, cliCod: nota.cliCod, pnvLin: rateioItem.pnvLin,
        linhas: rateioLinhas.map(l => ({ parcela: l.parcela, aplCod: l.novaAplCod || l.aplCod, percentual: parsePerc(l.novoPerc) })),
      })
      setSucesso(`Rateio da linha ${rateioItem.pnvLin} da nota ${nota.serie}/${nota.numero} alterado com sucesso.`)
      setRateioItem(null)
      await buscarDetalhe({ empcod: nota.empcod, serie: nota.serie, numero: nota.numero, cliCod: nota.cliCod })
    } catch (e: any) {
      setRateioErro(e?.response?.data?.error || 'Erro ao salvar o rateio.')
    } finally {
      setRateioSalvando(false)
    }
  }

  function limparResultado() {
    setNota(null); setLista(null)
    setNovaAplGeralCod(''); setNovaAplGeralDes(''); setOverrideApl({}); setOverrideServ({}); setAvisos([])
  }

  async function buscarDetalhe(ident: { chave?: string; empcod?: number; serie?: string; numero?: number; cliCod?: string }) {
    const r = await api.get('/erp/alterar-aplicacao-nota', { params: ident })
    setNota(r.data)
    setLista(null)
  }

  async function buscar() {
    setErro(''); setSucesso(''); limparResultado()
    const chaveDigitos = chave.replace(/\D/g, '')
    if (!chaveDigitos && !serie.trim() && !numero.trim() && !fornecedorCod) {
      setErro('Informe a Chave NF-e/CT-e, ou ao menos Série, Número ou Fornecedor.')
      return
    }
    setBuscando(true)
    try {
      if (chaveDigitos) {
        await buscarDetalhe({ chave: chaveDigitos })
        return
      }
      const params: Record<string, string | number> = { empcod: empresaCod ?? 1 }
      if (serie.trim()) params.serie = serie.trim()
      if (numero.trim()) params.numero = Number(numero)
      if (fornecedorCod) params.cliCod = fornecedorCod
      const r = await api.get('/erp/alterar-data-nota/lista', { params })
      const encontradas: NotaListaItem[] = r.data
      if (encontradas.length === 0) {
        setErro('Nenhuma nota encontrada.')
      } else if (encontradas.length === 1) {
        const n = encontradas[0]
        await buscarDetalhe({ empcod: n.empcod, serie: n.serie, numero: n.numero, cliCod: n.cliCod })
      } else {
        setLista(encontradas)
      }
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao buscar nota(s).')
    } finally {
      setBuscando(false)
    }
  }

  async function selecionarDaLista(item: NotaListaItem) {
    setErro(''); setBuscando(true)
    try {
      await buscarDetalhe({ empcod: item.empcod, serie: item.serie, numero: item.numero, cliCod: item.cliCod })
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao buscar a nota.')
    } finally {
      setBuscando(false)
    }
  }

  async function confirmar() {
    if (!nota) return
    const overridesApl = Object.entries(overrideApl).filter(([, v]) => v.cod)
    const overridesServ = Object.entries(overrideServ).filter(([, v]) => v.cod)
    if (!novaAplGeralCod && overridesApl.length === 0 && overridesServ.length === 0) {
      setErro('Informe a Nova Aplicação (geral), ou a aplicação/serviço individual de ao menos um item.')
      return
    }
    setErro(''); setSucesso(''); setAvisos([])
    setSalvando(true)
    try {
      const avisosTotais: string[] = []

      if (novaAplGeralCod) {
        const r = await api.post('/erp/alterar-aplicacao-nota', {
          empcod: nota.empcod, serie: nota.serie, numero: nota.numero, cliCod: nota.cliCod,
          escopo: 'nota', novaAplicacao: novaAplGeralCod,
        })
        avisosTotais.push(...(r.data?.avisos ?? []))
      }

      for (const [lin, v] of overridesApl) {
        const r = await api.post('/erp/alterar-aplicacao-nota', {
          empcod: nota.empcod, serie: nota.serie, numero: nota.numero, cliCod: nota.cliCod,
          escopo: 'linha', novaAplicacao: v.cod, pnvLin: Number(lin),
        })
        avisosTotais.push(...(r.data?.avisos ?? []))
      }

      for (const [lin, v] of overridesServ) {
        await api.post('/erp/alterar-aplicacao-nota/servico', {
          empcod: nota.empcod, serie: nota.serie, numero: nota.numero, cliCod: nota.cliCod,
          pnvLin: Number(lin), novoTipSer: v.cod,
        })
      }

      setSucesso(`Aplicação/Serviço da nota ${nota.serie}/${nota.numero} alterado com sucesso.`)
      setAvisos(avisosTotais)
      limparResultado()
      setChave(''); setSerie(''); setNumero(''); setFornecedorCod(''); setFornecedorDes('')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao alterar a aplicação da nota.')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>Altera Aplicação das Notas Fiscais</h2>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.9rem' }}>
          <Campo rotulo="Chave NF-e/CT-e" width={420}>
            <input style={{ ...inp, fontFamily: 'monospace' }} value={chave} maxLength={44}
              onChange={e => setChave(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && buscar()}
              placeholder="44 dígitos" />
          </Campo>
        </div>

        <div style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '-0.5rem 0 0.9rem' }}>— ou —</div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Série" width={90}>
            <input style={inp} value={serie} onChange={e => setSerie(e.target.value)} />
          </Campo>
          <Campo rotulo="Número" width={130}>
            <input style={inp} inputMode="numeric" value={numero} onChange={e => setNumero(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && buscar()} />
          </Campo>
          <Campo rotulo="Fornecedor (opcional)" width={340}>
            <LookupSelect
              endpoint="/erp/clientes/lista" codKey="cli_cod" descKey="cli_fan"
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des) => { setFornecedorCod(cod); setFornecedorDes(des) }}
              placeholder="Buscar fornecedor..." listWidth={320} somenteComTexto
            />
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={buscando} onClick={buscar} style={{ height: 32 }}>
            {buscando ? 'Buscando...' : 'Buscar'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}
      {sucesso && <div className="alert alert-success" style={{ marginBottom: '0.75rem' }}>✓ {sucesso}</div>}
      {avisos.map((a, i) => (
        <div key={i} className="alert alert-warning" style={{ marginBottom: '0.75rem' }}>⚠ {a}</div>
      ))}

      {lista && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
        }}>
          <div style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.6rem' }}>
            {lista.length} nota(s) encontrada(s) — clique na linha certa:
          </div>
          <table className="table" style={{ width: '100%', fontSize: '0.82rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center', width: 60 }}>Série</th>
                <th style={{ textAlign: 'center', width: 90 }}>Número</th>
                <th style={{ textAlign: 'left' }}>Fornecedor</th>
                <th style={{ textAlign: 'center', width: 70 }}>Modelo</th>
                <th style={{ textAlign: 'center', width: 100 }}>Emissão</th>
                <th style={{ textAlign: 'center', width: 100 }}>Recebimento</th>
                <th style={{ textAlign: 'right', width: 120 }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((it, idx) => (
                <tr key={`${it.empcod}-${it.serie}-${it.numero}-${it.cliCod}`}
                  onClick={() => selecionarDaLista(it)}
                  style={{ cursor: 'pointer', background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                  <td style={{ textAlign: 'center' }}>{it.serie}</td>
                  <td style={{ textAlign: 'center' }}>{it.numero}</td>
                  <td>{it.cliNom} ({it.cliCod})</td>
                  <td style={{ textAlign: 'center' }}>{it.modelo}</td>
                  <td style={{ textAlign: 'center' }}>{fmtDataBr(it.dataEmissao)}</td>
                  <td style={{ textAlign: 'center' }}>{fmtDataBr(it.dataRecebimento)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a6ea8' }}>{fmtMoeda(it.valorTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nota && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1rem', fontSize: '0.85rem' }}>
            <div><strong>Estab.:</strong> {nota.empcod} &nbsp; <strong>Série:</strong> {nota.serie} &nbsp; <strong>Número:</strong> {nota.numero}</div>
            <div><strong>Fornecedor:</strong> {nota.cliNom} ({nota.cliCod})</div>
            <div><strong>Modelo:</strong> {nota.modelo}</div>
            <div><strong>Emissão:</strong> {fmtDataBr(nota.dataEmissao)} &nbsp; <strong>Recebimento:</strong> {fmtDataBr(nota.dataRecebimento)}</div>
          </div>

          <div style={{
            background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8',
            borderRadius: 6, padding: '0.85rem 1rem', marginBottom: '1rem',
          }}>
            <Campo rotulo="Nova Aplicação (geral — aplica em todos os itens sem rateio)" width={340}>
              <LookupSelect
                endpoint="/erp/aplicacoes" codKey="apl_cod" descKey="apl_des"
                renderItem={renderAplicacao} buildDesc={descAplicacao}
                codValue={novaAplGeralCod} descValue={novaAplGeralDes}
                onSelect={(cod, des) => { setNovaAplGeralCod(cod); setNovaAplGeralDes(des) }}
                placeholder="Selecione..." listWidth={340}
              />
            </Campo>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.6rem' }}>
              Preenchendo a Aplicação/Serviço individual de um item na tabela abaixo, esse item usa o valor individual em vez da geral.
            </div>
          </div>

          <table className="table" style={{ width: '100%', fontSize: '0.8rem', marginBottom: '1rem' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'center', width: 50 }}>Lin</th>
                <th style={{ textAlign: 'center', width: 90 }}>Item</th>
                <th style={{ textAlign: 'left' }}>Descrição</th>
                <th style={{ textAlign: 'right', width: 90 }}>Qtde</th>
                <th style={{ textAlign: 'right', width: 110 }}>Valor</th>
                <th style={{ textAlign: 'left', width: 240 }}>Aplicação Atual</th>
                <th style={{ textAlign: 'left', width: 260 }}>Nova Aplicação (individual)</th>
                <th style={{ textAlign: 'left', width: 200 }}>Serviço Atual</th>
                <th style={{ textAlign: 'left', width: 260 }}>Novo Serviço (individual)</th>
              </tr>
            </thead>
            <tbody>
              {nota.itens.map((it, idx) => (
                <tr key={it.pnvLin} style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                  <td style={{ textAlign: 'center', color: '#6b7280' }}>{it.pnvLin}</td>
                  <td style={{ textAlign: 'center', color: '#6b7280' }}>{it.itsCod ?? '-'}</td>
                  <td>
                    {it.descricao}
                    {it.temRateio && (
                      <div style={{ fontSize: '0.7rem', color: '#b45309' }}>item com rateio por centro de custo</div>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>{it.quantidade.toLocaleString('pt-BR', { minimumFractionDigits: 3 })}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#1a6ea8' }}>{fmtMoeda(it.valorTotal)}</td>
                  <td>{it.temRateio ? <span style={{ color: '#6b7280' }}>(várias — ver rateio)</span> : <>{it.aplCodAtual} {it.aplDesAtual && `- ${it.aplDesAtual}`}</>}</td>
                  <td>
                    {it.temRateio ? (
                      <button className="btn btn-ghost btn-sm" onClick={() => abrirRateio(it)}
                        style={{ border: '1px solid #1a6ea8', color: '#1a6ea8', fontWeight: 600 }}>
                        Alterar rateio
                      </button>
                    ) : (
                    <LookupSelect
                      endpoint="/erp/aplicacoes" codKey="apl_cod" descKey="apl_des"
                      renderItem={renderAplicacao} buildDesc={descAplicacao}
                      codValue={overrideApl[it.pnvLin]?.cod ?? ''} descValue={overrideApl[it.pnvLin]?.des ?? ''}
                      onSelect={(cod, des) => setOverrideApl(prev => cod ? { ...prev, [it.pnvLin]: { cod, des } } : (({ [it.pnvLin]: _, ...rest }) => rest)(prev))}
                      placeholder="(usar a geral)" listWidth={320}
                    />
                    )}
                  </td>
                  <td>{it.tipSerAtual ?? '-'} {it.tipSerDesAtual && `- ${it.tipSerDesAtual}`}</td>
                  <td>
                    <LookupSelect
                      endpoint="/erp/serman/lista" codKey="sercod" descKey="serdes"
                      renderItem={renderServico} buildDesc={descServico}
                      codValue={overrideServ[it.pnvLin]?.cod ?? ''} descValue={overrideServ[it.pnvLin]?.des ?? ''}
                      onSelect={(cod, des) => setOverrideServ(prev => cod ? { ...prev, [it.pnvLin]: { cod, des } } : (({ [it.pnvLin]: _, ...rest }) => rest)(prev))}
                      placeholder="(manter)" listWidth={320}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button className="btn btn-primary btn-sm" disabled={salvando} onClick={confirmar} style={{ height: 32 }}>
            {salvando ? 'Salvando...' : 'Confirmar Alteração'}
          </button>
        </div>
      )}

      {rateioItem && nota && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 8, padding: '1.25rem 1.4rem',
            width: 980, maxWidth: '96vw', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: 700, color: '#1a6ea8' }}>
              Altera Aplicação dos Rateios
            </h3>
            <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.8rem' }}>
              Nota {nota.serie}/{nota.numero} — Linha {rateioItem.pnvLin}: {rateioItem.descricao} — R$ {fmtMoeda(rateioTotal)}.
              Troque a aplicação, o percentual ou os dois. Mudando algum percentual, o valor de cada linha é recalculado.
            </div>

            {rateioLinhas.length === 0 && !rateioErro && <div style={{ padding: '1rem 0', color: '#6b7280' }}>Carregando...</div>}

            {rateioLinhas.length > 0 && (
              <table className="table" style={{ width: '100%', fontSize: '0.8rem', marginBottom: '0.6rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'center', width: 50 }}>Parc</th>
                    <th style={{ textAlign: 'center', width: 90 }}>Data</th>
                    <th style={{ textAlign: 'left' }}>Centro de Custo</th>
                    <th style={{ textAlign: 'left' }}>Aplicação Atual</th>
                    <th style={{ textAlign: 'left', width: 250 }}>Nova Aplicação</th>
                    <th style={{ textAlign: 'right', width: 100 }}>%</th>
                    <th style={{ textAlign: 'right', width: 110 }}>Valor R$</th>
                  </tr>
                </thead>
                <tbody>
                  {rateioLinhas.map((l, idx) => (
                    <tr key={l.parcela}>
                      <td style={{ textAlign: 'center' }}>{l.parcela}</td>
                      <td style={{ textAlign: 'center' }}>{fmtDataBr(l.data)}</td>
                      <td>{l.centroCustoCod} {l.centroCustoDes && `- ${l.centroCustoDes}`}</td>
                      <td>{l.aplCod} {l.aplDes && `- ${l.aplDes}`}</td>
                      <td>
                        <LookupSelect
                          endpoint="/erp/aplicacoes" codKey="apl_cod" descKey="apl_des"
                          renderItem={renderAplicacao} buildDesc={descAplicacao}
                          codValue={l.novaAplCod} descValue={l.novaAplDes}
                          onSelect={(cod, des) => setRateioLinhas(ls => ls.map((x, i) => i === idx ? { ...x, novaAplCod: cod, novaAplDes: des } : x))}
                          placeholder="(manter)" listWidth={300}
                        />
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <input style={{ ...inp, width: 85, textAlign: 'right' }} value={l.novoPerc}
                          onChange={e => setRateioLinhas(ls => ls.map((x, i) => i === idx ? { ...x, novoPerc: e.target.value.replace(/[^\d,]/g, '') } : x))} />
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {fmtMoeda(valorLinhaRateio(idx))}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>Total</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: rateioPercOk ? '#15803d' : '#b91c1c' }}>
                      {rateioSomaPerc.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 700 }}>{fmtMoeda(rateioTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            )}

            {rateioErro && <div className="alert alert-danger" style={{ marginBottom: '0.6rem' }}>{rateioErro}</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn btn-ghost btn-sm" disabled={rateioSalvando} onClick={() => setRateioItem(null)}>Cancelar</button>
              <button className="btn btn-primary btn-sm" disabled={rateioSalvando || rateioLinhas.length === 0 || !rateioPercOk} onClick={salvarRateio}>
                {rateioSalvando ? 'Salvando...' : 'Salvar rateio'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
